import { faArrowRight, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'antd';
import cn from 'classnames';
import { Link } from 'react-router-dom';

import type {
  LeadCardHistoryActionItemData,
  LeadCardHistoryItemData,
} from '@app/components/LeadCard/data';
import { addLeadingZeroes } from '@app/utils/addLeadingZeroes';

import { useLeadTechnicalMessage } from '../hooks/useLeadTechnicalMessage';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { FC } from 'react';

import './LeadTechnicalMessage.scss';

interface LeadTechnicalMessageProps {
  item: LeadCardHistoryItemData;
}

const formatTechnicalTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
};

const hasActionItemValue = ({
  ValueOld,
  ValueNew,
}: LeadCardHistoryActionItemData): boolean => Boolean(ValueOld || ValueNew);

export const LeadTechnicalMessage: FC<LeadTechnicalMessageProps> = ({
  item,
}) => {
  const actionItems = (item.ActionItems ?? []).filter(hasActionItemValue);
  const formattedTimestamp = formatTechnicalTimestamp(item.Timestamp);
  const {
    actionTextParts,
    isCommentAction,
    objectLink,
    openObjectHandler,
    openPaidRecordHandler,
    shouldShowPaidRecordLink,
  } = useLeadTechnicalMessage({ item });

  const objectButtonLabel =
    item.ObjectType === 'Record'
      ? addLeadingZeroes(item.ObjectId.toString())
      : actionTextParts?.objectLabel;

  const actionTextContent = actionTextParts ? (
    <>
      {actionTextParts.prefix}
      {actionTextParts.objectLabelPrefix}
      {objectLink ? (
        <Link className="LeadTechnicalMessage__link" to={objectLink}>
          {actionTextParts.objectLabel}
        </Link>
      ) : openObjectHandler ? (
        <Button
          type="link"
          className="LeadTechnicalMessage__link-button"
          onClick={openObjectHandler}
        >
          {objectButtonLabel}
        </Button>
      ) : (
        actionTextParts.objectLabel
      )}
      {actionTextParts.suffix}
    </>
  ) : (
    <>
      {item.ActionText}
      {shouldShowPaidRecordLink && (
        <>
          {' '}
          <Button
            type="link"
            className="LeadTechnicalMessage__link-button"
            onClick={openPaidRecordHandler}
          >
            <span data-testid="record-id">
              {addLeadingZeroes(item.ObjectId.toString())}
            </span>
          </Button>
        </>
      )}
    </>
  );

  return (
    <div
      className={cn('LeadTechnicalMessage', {
        'LeadTechnicalMessage--comment': isCommentAction,
      })}
      data-action-id={item.IdS ?? ''}
    >
      <div className="LeadTechnicalMessage__header">
        <span className="LeadTechnicalMessage__icon">
          {item.Icon ? (
            <FontAwesomeIcon icon={item.Icon as IconProp} />
          ) : (
            <FontAwesomeIcon icon={faCircleInfo} />
          )}
        </span>
        <div className="LeadTechnicalMessage__header-text">
          {item.ActionUser && (
            <span className="LeadTechnicalMessage__user">
              {item.ActionUser}{' '}
            </span>
          )}
          <span>{actionTextContent}</span>
          <span className="LeadTechnicalMessage__timestamp">
            {' - '}
            {formattedTimestamp}
          </span>
        </div>
      </div>

      {actionItems.length > 0 && (
        <div className="LeadTechnicalMessage__fragments">
          {actionItems.map((actionItem, index) => (
            <div
              className="LeadTechnicalMessage__fragment"
              data-testid="history-action-item"
              key={`${actionItem.Property ?? ''}_${index}`}
            >
              <span className="LeadTechnicalMessage__fragment-icon">
                {actionItem.Icon && (
                  <FontAwesomeIcon icon={actionItem.Icon as IconProp} />
                )}
              </span>
              <span
                className="LeadTechnicalMessage__fragment-old"
                data-testid="action-item-old-value"
              >
                {actionItem.ValueOld}
              </span>
              {actionItem.ValueOld && (
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="LeadTechnicalMessage__fragment-arrow"
                />
              )}
              <span
                className="LeadTechnicalMessage__fragment-new"
                data-testid="action-item-new-value"
              >
                {actionItem.ValueNew}
              </span>
              {actionItem.Property && (
                <span
                  className="LeadTechnicalMessage__fragment-property"
                  data-testid="action-item-property"
                >
                  {actionItem.Property}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
